import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import QuantityEditor from "./quantity_editor";
import IconButton from "./icon_button";
import StatusTooltip from "./status_tooltip";

const MANDATORY_STATUS = 'mandatory';

const Triangle = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  display : inline-block;
  height : 0;
  width : 0;
  border-right : 10px solid transparent;
  border-bottom : 10px solid #626469;
  border-left : 10px solid transparent;

  transition: transform 0.3s;
  transform: ${props => props.showSubAccessories ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const EditAndDeleteWrapper = styled.div`
  display: flex;
`;

const SelectedOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 10px;
  background-color: white;
  margin-bottom: 5px;
  box-shadow: 0px 0px 4px 0px rgba(98, 100, 105, 0.20);
`;

const SelectedOptionLineWithArrow = styled.div`
  display: flex;
`;

const SelectedOptionLine = styled.div`
  display: flex;
  flex: 1 1 0px;
  align-items: center;
  min-height: 40px;
  box-sizing: content-box;
  overflow: hidden;

  ${props => props.showPadding ? 'padding: 10px 0px;' : ''}
  ${props => props.isSubAccessory ? ' border-top: 1px solid #DBDBDB;' : ''}

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    flex-direction: column;
    align-items: unset;
  }
`;

const SelectedOptionType = styled.span`
  font-weight: 700;
  font-size: 14px;
  border-left: 2px solid rgba(98, 100, 105, 0.2);
  margin-left: 10px;
  padding-left: 10px;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    margin: 5px 0px;
    padding-left: 0px;
    border: unset;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  margin-left: 10px;
  align-items: center;

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    margin-left: 0px;
    margin-top: 10px;
    justify-content: space-between;
  }
`;

const SelectedOptionDescription = styled.span`
  font-weight: 400;
  font-size: 14px;
  border-left: 2px solid rgba(98, 100, 105, 0.2);
  margin-left: 10px;
  padding-left: 10px;
  flex: 1 1 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    border-left: unset;
    margin-left: 0px;
    margin-top: 10px;
    padding-left: 0px;
    text-overflow: unset;
    white-space: unset;

    flex: unset;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
  }
`;

const LinkWrapper = styled.div`
  display: inline-flex;
  font-weight: 700;
`;

const StatusAndTitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SubAccessoryIndicatorWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  box-sizing: content-box;
  ${props => props.isFirst ? 'cursor: pointer;' : ''}
`;

const VerticalLine = styled.div`
  position: absolute;
  width: 1px;
  box-sizing: content-box;
  left: 50%;
  top: ${props => props.isFirst ? '50%' : '0px'};
  bottom: ${props => props.isLast ? '50%' : '0px'};
  background-color: #DBDBDB;
  transform: translateX(-50%);
`;

const HorizontalLine = styled.div`
  position: absolute;
  height: 1px;
  top: 50%;
  left: 50%;
  right: 0px;
  box-sizing: content-box;
  background-color: #DBDBDB;
`;

const EmptyTooltipSpace = styled.div`
  min-width: 30px;
  max-width: 30px;
  margin-right: 5px;
`;

class SelectedOption extends React.Component {
  static propTypes = {
    renderProductLink: PropTypes.func,
    selectedOption: PropTypes.object,
    moduleItem: PropTypes.object,
    isSaving: PropTypes.bool,
    getLabel:PropTypes.func,
    primaryColor: PropTypes.string,
    primaryTextColor: PropTypes.string,
    onViewAccessory: PropTypes.func,
    categoryModalKey: PropTypes.number
  };

  constructor(props) {
    super(props);
    this.state = {
      showSubAccessories: false
    };
  }

  removeItem = (item) => {
    if (item.onRemove) {
      item.onRemove(this.props.moduleItem.deviceIdentification, item.optionIdentification);
    }
  }

  optionIsMandatory = (option) => {
    return option.status === MANDATORY_STATUS;
  }

  renderQuantity = (item) => {

    return (
      <QuantityEditor
        id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-selectedoptions`}
        primaryColor={this.props.primaryColor}
        primaryTextColor={this.props.primaryTextColor}
        isSaving={this.props.isSaving}
        quantity={item.quantity}
        canIncrease={item.canIncrease && !this.optionIsMandatory(item)}
        onIncrease={() => { item.onIncrease(this.props.moduleItem.deviceIdentification, item.optionIdentification, 1); }}
        canDecrease={item.canDecrease && !this.optionIsMandatory(item)}
        onDecrease={() => {
          if (item.quantity === 1) {
            this.removeItem(item);
          } else  {
            item.onDecrease(this.props.moduleItem.deviceIdentification, item.optionIdentification, 1);
          }
        }}>
      </QuantityEditor>);
  }

  renderSubAccessoryQuantity = (subAccessory, item) => {

    return (
      <QuantityEditor
        id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-${subAccessory.subAccessoryIdentification.identificationKey}-selectedsubaccessory`}
        primaryColor={this.props.primaryColor}
        primaryTextColor={this.props.primaryTextColor}
        isSaving={this.props.isSaving}
        quantity={subAccessory.quantity}
        canIncrease={subAccessory.canIncrease}
        onIncrease={() => {
          if (this.props.moduleItem?.onAddSubAccessories) {
            this.props.moduleItem.onAddSubAccessories(this.props.moduleItem.deviceIdentification, item.optionIdentification, subAccessory.subAccessoryIdentification, 1);
          }
        }}
        canDecrease={subAccessory.canDecrease}
        onDecrease={() => {
          if (subAccessory.quantity === 1) {
            if (this.props.moduleItem?.onRemoveSubAccessories) {
              this.props.moduleItem.onRemoveSubAccessories(this.props.moduleItem.deviceIdentification, item.optionIdentification, subAccessory.subAccessoryIdentification);
            }
          } else  {
            if (this.props.moduleItem?.onDecreaseSubAccessories) {
              this.props.moduleItem.onDecreaseSubAccessories(this.props.moduleItem.deviceIdentification, item.optionIdentification, subAccessory.subAccessoryIdentification, 1);
            }
          }
        }}>
      </QuantityEditor>);
  }

  renderActionButtons = (item, categoryModalKey) => {
    return (
      <EditAndDeleteWrapper>
        {item.canShowInModal && <IconButton id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-selectedoptions-edit`} icon="edit" size={40} padding={7} color={this.props.primaryColor} onClick={() => {
          this.props.onViewAccessory(item, categoryModalKey, item.filterStatusOnEdit, item.disablePreviousOnEdit || false);
        }} disabled={this.props.isSaving}></IconButton>}
        {item.canRemove && <IconButton id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-selectedoptions-remove`} icon="remove" size={40} padding={7} color={'#626469'} onClick={() => {
          this.removeItem(item);
        }} disabled={this.props.isSaving}>
        </IconButton>}
      </EditAndDeleteWrapper>
    );
  }

  // It is on purpose that we choose to display the "canShowInModal" based on the item attribute (and not the sub accessory).
  renderSubAccessoriesActionButtons = (subAcc, item, categoryModalKey) => {
    return (
      <EditAndDeleteWrapper>
        {item.canShowInModal && <IconButton id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-${subAcc.subAccessoryIdentification.identificationKey}-selectedsubaccessory-edit`} icon="edit" size={40} padding={7} color={this.props.primaryColor} onClick={() => {
          this.props.onViewAccessory(item, categoryModalKey, item.filterStatusOnEdit, item.disablePreviousOnEdit || false);
        }} disabled={this.props.isSaving}></IconButton>}
        {subAcc.canRemove && <IconButton id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-${subAcc.subAccessoryIdentification.identificationKey}-selectedsubaccessory-remove`} icon="remove" size={40} padding={7} color={'#626469'} onClick={() => {
          if (this.props.moduleItem?.onRemoveSubAccessories) {
            this.props.moduleItem.onRemoveSubAccessories(this.props.moduleItem.deviceIdentification, item.optionIdentification, subAcc.subAccessoryIdentification);
          }
        }} disabled={this.props.isSaving}>
        </IconButton>}
      </EditAndDeleteWrapper>
    );
  }

  renderEventualTootipEmptySpace = () => {
    return (this.props.moduleItem.alignOptionLinks ? <EmptyTooltipSpace></EmptyTooltipSpace> : <></>);
  }

  render() {
    const item = this.props.selectedOption;
    const hasSubAccessories = (item.subAccessories && item.subAccessories.length > 0) || false;

    return (
      <SelectedOptionWrapper>
        <SelectedOptionLineWithArrow>
          {hasSubAccessories && <SubAccessoryIndicatorWrapper id={`${this.props.moduleItem.deviceIdentification.identificationKey}-${item.optionIdentification.identificationKey}-subaccessories-arrow`} isFirst={true} onClick={() => { this.setState({showSubAccessories: !this.state.showSubAccessories}); }}>
            <Triangle showSubAccessories={this.state.showSubAccessories}></Triangle>
            {this.state.showSubAccessories && <VerticalLine isFirst={true} isLast={false}></VerticalLine>}
          </SubAccessoryIndicatorWrapper>}
          <SelectedOptionLine showPadding={hasSubAccessories && this.state.showSubAccessories} mobileBreakpoint={this.props.mobileBreakpoint}>
            <StatusAndTitleWrapper>
              {item.status && item.status === 'normal' ? this.renderEventualTootipEmptySpace() : <StatusTooltip status={item.status} getLabel={this.props.getLabel} cursor={'help'}></StatusTooltip>}
              <LinkWrapper>
                {this.props.renderProductLink(item.id)}
              </LinkWrapper>
            </StatusAndTitleWrapper>
            {item.type && <SelectedOptionType mobileBreakpoint={this.props.mobileBreakpoint}>{item.type}</SelectedOptionType>}
            <SelectedOptionDescription mobileBreakpoint={this.props.mobileBreakpoint} title={item.description}>{item.description}</SelectedOptionDescription>
            <ActionsWrapper mobileBreakpoint={this.props.mobileBreakpoint}>
              {this.renderQuantity(item)}
              {this.renderActionButtons(item, this.props.categoryModalKey)}
            </ActionsWrapper>
          </SelectedOptionLine>
        </SelectedOptionLineWithArrow>
        {item.subAccessories && this.state.showSubAccessories && item.subAccessories.map((subAcc, idx) => {
          return (
            <SelectedOptionLineWithArrow key={idx}>
              <SubAccessoryIndicatorWrapper>
                <VerticalLine isFirst={false} isLast={idx === (item.subAccessories.length - 1)}></VerticalLine>
                {idx < item.subAccessories.length && <HorizontalLine></HorizontalLine>}
              </SubAccessoryIndicatorWrapper>
              <SelectedOptionLine showPadding={true} isSubAccessory={true} mobileBreakpoint={this.props.mobileBreakpoint}>
                <StatusAndTitleWrapper>
                  {subAcc.status && subAcc.status === 'normal' ? this.renderEventualTootipEmptySpace() : <StatusTooltip status={subAcc.status} getLabel={this.props.getLabel} cursor={'help'}></StatusTooltip>}
                  <LinkWrapper>
                    {this.props.renderProductLink(subAcc.id)}
                  </LinkWrapper>
                </StatusAndTitleWrapper>
                {subAcc.type && <SelectedOptionType mobileBreakpoint={this.props.mobileBreakpoint}>{subAcc.type}</SelectedOptionType>}
                <SelectedOptionDescription mobileBreakpoint={this.props.mobileBreakpoint} title={subAcc.description}>{subAcc.description}</SelectedOptionDescription>
                <ActionsWrapper mobileBreakpoint={this.props.mobileBreakpoint}>
                  {this.renderSubAccessoryQuantity(subAcc, item)}
                  {this.renderSubAccessoriesActionButtons(subAcc, item, this.props.categoryModalKey)}
                </ActionsWrapper>
              </SelectedOptionLine>
            </SelectedOptionLineWithArrow>
          );
        })}
      </SelectedOptionWrapper>
    );
  }
}

export default SelectedOption;
